import React, { useCallback, useEffect, useState } from 'react';
import { WeekFilter } from '../ui/week-filter';
import { DateText } from '../ui/week-badge';
import { matchesWeek } from '../../lib/weekFilter';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Camera, Plus, Printer, Search, Trash2, Truck, X } from 'lucide-react';
import useDeepShortcut from '../../hooks/useDeepShortcut';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  supplier_id: '', delivery_date: new Date().toISOString().slice(0, 10),
  delivery_note_number: '', currency: 'PLN', storage_location_id: '', notes: '', received_by: '',
};

/** Which corrections can be pushed down to every tool in the batch. The user
 *  ticks them, so an edit never quietly overwrites something that was
 *  deliberately different on one unit. */
const PUSHABLE = [
  { field: 'supplier_id', labelKey: 'inv.form.supplier' },
  { field: 'delivery_date', labelKey: 'inv.deliveries.deliveryDate' },
  { field: 'currency', labelKey: 'inv.form.currency' },
  { field: 'storage_location_id', labelKey: 'inv.form.storageLocation' },
];

const DeliveriesList = ({ suppliers = [], storageLocations = [], currencies = [], isAdmin, navigateTo, onBookInto }) => {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [weekFilter, setWeekFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [editing, setEditing] = useState(null);
  const [applyTo, setApplyTo] = useState([]);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const currencyList = currencies.length ? currencies : ['PLN', 'EUR', 'GBP', 'USD'];

  const fetchRows = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/inventory/deliveries`, { params: search ? { q: search } : {} });
      setRows(r.data);
    } catch { toast.error(t('inv.deliveries.loadFailed')); }
    setLoading(false);
  }, [search, t]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Dashboard shortcuts: an open batch, or the New delivery form.
  useDeepShortcut({
    active: !!detail,
    tab: 'inventory',
    context: { invSubTab: 'deliveries', focusDeliveryId: detail?.id },
    label: detail?.reference,
    subtitle: t('inv.tabs.deliveries'),
    icon: 'deliveries',
    perms: ['inventory.view'],
    priority: 3,
    focusKey: 'focusDeliveryId',
    onFocus: (id) => openDetail(id),
  });
  useDeepShortcut({
    active: formOpen && !editing,
    tab: 'inventory',
    context: { invSubTab: 'deliveries', create: 'delivery' },
    label: t('shortcuts.qc.delivery'),
    icon: 'deliveries',
    perms: ['inventory.edit'],
    priority: 4,
    focusKey: 'create',
    onFocus: (v) => { if (v === 'delivery') openCreate(); },
  });

  const openCreate = () => {
    setEditing(null); setApplyTo([]); setForm({ ...EMPTY }); setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setApplyTo([]);
    setForm({
      supplier_id: row.supplier_id || '', delivery_date: row.delivery_date || '',
      delivery_note_number: row.delivery_note_number || '', currency: row.currency || 'PLN',
      storage_location_id: row.storage_location_id || '', notes: row.notes || '',
      received_by: row.received_by || '',
    });
    setFormOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const r = await axios.put(`${API}/inventory/deliveries/${editing.id}`, { ...form, apply_to_items: applyTo });
        toast.success(r.data.items_updated
          ? t('inv.deliveries.correctedWithItems', { count: r.data.items_updated })
          : t('inv.deliveries.corrected'));
      } else {
        const r = await axios.post(`${API}/inventory/deliveries`, form);
        toast.success(t('inv.deliveries.created', { ref: r.data.reference }));
      }
      setFormOpen(false);
      fetchRows();
      if (detail) openDetail(detail.id);
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.saveFailed')); }
    setSaving(false);
  };

  const openDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/inventory/deliveries/${id}`);
      setDetail(r.data);
    } catch { toast.error(t('inv.deliveries.loadFailed')); }
  };

  const printNote = (row) => {
    window.open(`${API}/inventory/deliveries/${row.id}/note.pdf?lang=${i18n.language === 'pl' ? 'pl' : 'en'}`, '_blank');
  };

  const confirmDelete = async () => {
    try {
      const r = await axios.delete(`${API}/inventory/deliveries/${deleteTarget.id}`);
      toast.success(t('inv.deliveries.archived', { count: r.data.items_detached }));
      setDeleteTarget(null); setDetail(null); fetchRows();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.deleteFailed')); }
  };

  const detachItem = async (assetId) => {
    try {
      await axios.delete(`${API}/inventory/deliveries/${detail.id}/items/${assetId}`);
      toast.success(t('inv.deliveries.itemRemoved'));
      openDetail(detail.id); fetchRows();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.saveFailed')); }
  };

  const toggleApply = (field) => setApplyTo((a) => (
    a.includes(field) ? a.filter((x) => x !== field) : [...a, field]));

  return (
    <div className="space-y-4" data-testid="deliveries-list">
      <Card>
        <CardContent className="pt-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input className="pl-8" placeholder={t('inv.deliveries.searchPlaceholder')}
              value={search} onChange={(e) => setSearch(e.target.value)} data-testid="delivery-search" />
          </div>
          <WeekFilter value={weekFilter} onChange={setWeekFilter} testId="delivery-week-filter" />
          {isAdmin && (
            <Button onClick={openCreate} data-testid="create-delivery-btn">
              <Plus className="w-4 h-4 mr-1" /> {t('inv.deliveries.newDelivery')}
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Truck className="h-4 w-4" /> {t('inv.deliveries.title')} ({rows.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="deliveries-empty">
              {t('inv.deliveries.empty')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">{t('inv.deliveries.reference')}</th>
                    <th className="py-2 pr-3">{t('inv.form.supplier')}</th>
                    <th className="py-2 pr-3">{t('inv.deliveries.deliveryDate')}</th>
                    <th className="py-2 pr-3">{t('inv.deliveries.noteNumber')}</th>
                    <th className="py-2 pr-3">{t('inv.form.storageLocation')}</th>
                    <th className="py-2 pr-3">{t('inv.deliveries.items')}</th>
                    <th className="py-2 pr-3 text-right">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.filter((r) => matchesWeek(weekFilter, r.delivery_date)).map((r) => (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer"
                      onClick={() => openDetail(r.id)} data-testid={`delivery-row-${r.reference}`}>
                      <td className="py-2 pr-3 font-mono text-xs font-medium">{r.reference}</td>
                      <td className="py-2 pr-3">{r.supplier_name || '—'}</td>
                      <td className="py-2 pr-3 whitespace-nowrap"><DateText value={r.delivery_date} /></td>
                      <td className="py-2 pr-3">{r.delivery_note_number || '—'}</td>
                      <td className="py-2 pr-3">{r.storage_location_name || '—'}</td>
                      <td className="py-2 pr-3"><Badge variant="secondary">{r.item_count}</Badge></td>
                      <td className="py-2 pr-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => printNote(r)}
                            title={t('inv.deliveries.printNote')} data-testid={`print-delivery-${r.reference}`}>
                            <Printer className="h-3.5 w-3.5" />
                          </Button>
                          {isAdmin && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600"
                              onClick={() => setDeleteTarget(r)} data-testid={`delete-delivery-${r.reference}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / correct */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="delivery-form-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing ? t('inv.deliveries.correctDelivery', { ref: editing.reference })
                : t('inv.deliveries.newDelivery')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t('inv.form.supplier')}</Label>
                <Select value={form.supplier_id} onValueChange={(v) => setForm((f) => ({ ...f, supplier_id: v }))}>
                  <SelectTrigger data-testid="delivery-supplier-select"><SelectValue placeholder={t('inv.form.select')} /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t('inv.deliveries.deliveryDate')}</Label>
                <Input type="date" value={form.delivery_date}
                  onChange={(e) => setForm((f) => ({ ...f, delivery_date: e.target.value }))}
                  data-testid="delivery-date-input" />
              </div>
              <div>
                <Label className="text-xs">{t('inv.deliveries.noteNumber')}</Label>
                <Input value={form.delivery_note_number}
                  onChange={(e) => setForm((f) => ({ ...f, delivery_note_number: e.target.value }))}
                  placeholder={t('auto.inv.wz20260912')} data-testid="delivery-note-number-input" />
              </div>
              <div>
                <Label className="text-xs">{t('inv.form.currency')}</Label>
                <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                  <SelectTrigger data-testid="delivery-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {currencyList.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">{t('inv.form.storageLocation')}</Label>
                <Select value={form.storage_location_id} onValueChange={(v) => setForm((f) => ({ ...f, storage_location_id: v }))}>
                  <SelectTrigger data-testid="delivery-location-select"><SelectValue placeholder={t('inv.form.select')} /></SelectTrigger>
                  <SelectContent>
                    {storageLocations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">{t('inv.deliveries.notes')}</Label>
                <Textarea rows={2} value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  data-testid="delivery-notes-input" />
              </div>
            </div>

            {/* Correcting an existing batch: choose what to push down. */}
            {editing && editing.item_count > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="delivery-apply-box">
                <p className="text-xs font-medium text-amber-900">
                  {t('inv.deliveries.applyToItems', { count: editing.item_count })}
                </p>
                <p className="text-[11px] text-amber-800 mb-2">{t('inv.deliveries.applyHint')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {PUSHABLE.map((p) => (
                    <label key={p.field} className="flex items-center gap-2 text-xs cursor-pointer">
                      <Checkbox checked={applyTo.includes(p.field)} onCheckedChange={() => toggleApply(p.field)}
                        data-testid={`apply-${p.field}`} />
                      {t(p.labelKey)}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)} data-testid="cancel-delivery-form">
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-delivery-btn">
                {saving ? t('inv.common.saving') : editing ? t('inv.common.update') : t('inv.common.create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="delivery-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Truck className="h-4 w-4" /> {detail.reference}
                  <Badge variant="secondary">{detail.item_count} {t('inv.deliveries.items')}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                {[
                  [t('inv.form.supplier'), detail.supplier_name],
                  [t('inv.deliveries.deliveryDate'), detail.delivery_date && <DateText value={detail.delivery_date} />],
                  [t('inv.deliveries.noteNumber'), detail.delivery_note_number],
                  [t('inv.form.storageLocation'), detail.storage_location_name],
                  [t('inv.form.currency'), detail.currency],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-xs text-muted-foreground">{k}</div>
                    <div className="font-medium">{v || '—'}</div>
                  </div>
                ))}
              </div>
              {detail.notes && <p className="text-sm text-muted-foreground">{detail.notes}</p>}

              <div className="rounded-md border divide-y" data-testid="delivery-items-list">
                {(detail.items || []).length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground text-center">{t('inv.deliveries.noItems')}</p>
                ) : detail.items.map((it) => (
                  <div key={it.id} className="flex items-center gap-3 px-3 py-2 text-sm"
                    data-testid={`delivery-item-${it.inventory_number}`}>
                    <span className="font-mono text-xs text-muted-foreground w-20 shrink-0">{it.inventory_number}</span>
                    <span className="flex-1 min-w-0 truncate">{it.name}</span>
                    <span className="hidden sm:block text-xs text-muted-foreground font-mono">{it.serial_number || '—'}</span>
                    {isAdmin && (
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground"
                        onClick={() => detachItem(it.id)} title={t('inv.deliveries.removeItem')}
                        data-testid={`detach-item-${it.inventory_number}`}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => printNote(detail)} data-testid="detail-print-note-btn">
                  <Printer className="h-4 w-4 mr-1" /> {t('inv.deliveries.printNote')}
                </Button>
                {isAdmin && (
                  <Button variant="outline" onClick={() => openEdit(detail)} data-testid="detail-correct-btn">
                    {t('inv.deliveries.correct')}
                  </Button>
                )}
                {/* The whole point of a batch: go straight to scanning plate
                    after plate, with every tool landing in this delivery. */}
                {isAdmin && (
                  <Button onClick={() => { setDetail(null); onBookInto?.(detail); }}
                    data-testid="detail-book-into-btn">
                    <Camera className="h-4 w-4 mr-1" /> {t('inv.deliveries.scanInto')}
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delivery-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('inv.deliveries.archiveTitle', { ref: deleteTarget?.reference })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('inv.deliveries.archiveHint', { count: deleteTarget?.item_count || 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delivery-delete-cancel">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delivery-delete-confirm">
              {t('inv.deliveries.archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DeliveriesList;
